/**
 * Persian text folding, for matching rather than for display.
 *
 * ## Why this is not the same as "correct Persian"
 *
 * The `persian` skill's linter normalises text so it *reads* right: it inserts ZWNJ into می‌رود,
 * turns quotes into «…», and converts Latin digits to ۰–۹. Every one of those is the wrong
 * direction here. A search index wants variants **collapsed**, so that two spellings a reader
 * cannot tell apart become the same string — which means removing ZWNJ, not inserting it, and
 * converting ۰–۹ to Latin, not away from it.
 *
 * The observed failure this exists for: a real user searched `سایبرپانک` and got 0 results while
 * 542 cyberpunk prompts sat in the library. Folding alone does not fix that — the library is in
 * English — but nothing else works reliably until identical-looking Persian is identical bytes.
 *
 * ## What gets folded, and why each one matters
 *
 * - **Arabic vs Persian letters.** ي/ك are the Arabic forms of ی/ک. Arabic keyboards, older
 *   corpora and copy-paste from Arabic sites all produce them; they render nearly identically and
 *   compare unequal. Also ى (alef maksura) and ة (teh marbuta).
 * - **Alef and hamza forms.** أ إ آ ٱ all fold to ا, ؤ to و, ئ to ی. Users do not type the hamza
 *   consistently and neither do sources.
 * - **Diacritics (harakat) and tatweel.** Optional marks and a decorative stretching character.
 *   Both are invisible to meaning and fatal to equality.
 * - **Zero-width characters.** ZWNJ is the half-space that makes نیم‌فاصله correct and makes
 *   `نیم‌فاصله !== نیمفاصله`. For matching, both must be the same. ZWJ and the bidi marks
 *   (LRM/RLM/ALM) get pasted in invisibly and break comparisons in ways nobody can see.
 * - **Digits.** Persian ۰–۹ and Arabic-Indic ٠–٩ fold to Latin so "midjourney ۵" finds
 *   "midjourney 5".
 */

/** Arabic letterforms that render as their Persian counterparts. */
const LETTER_FOLD: Record<string, string> = {
  ي: 'ی', // ARABIC YEH      -> PERSIAN YEH  ي -> ی
  ى: 'ی', // ALEF MAKSURA    -> PERSIAN YEH  ى -> ی
  ك: 'ک', // ARABIC KAF      -> KEHEH        ك -> ک
  ة: 'ه', // TEH MARBUTA     -> HEH          ة -> ه
  أ: 'ا', // ALEF W/ HAMZA   -> ALEF         أ -> ا
  إ: 'ا', // ALEF W/ HAMZA B -> ALEF         إ -> ا
  آ: 'ا', // ALEF W/ MADDA   -> ALEF         آ -> ا
  ٱ: 'ا', // ALEF WASLA      -> ALEF         ٱ -> ا
  ؤ: 'و', // WAW W/ HAMZA    -> WAW          ؤ -> و
  ئ: 'ی', // YEH W/ HAMZA    -> PERSIAN YEH  ئ -> ی
};

/** A class built from the fold table's keys, so the two can never disagree. */
const FOLD_PATTERN = new RegExp(`[${Object.keys(LETTER_FOLD).join('')}]`, 'g');

/**
 * Characters removed outright.
 *
 * `ـ` is tatweel; `ً-ْ` and `ٰ` are the harakat; `​-‏` covers ZWSP,
 * ZWNJ, ZWJ and the bidi marks; `؜` is the Arabic letter mark; `ٕٔ` are combining
 * hamzas left behind after the letter folds above.
 */
const STRIP = /[ـً-ْٰٕٔ​-‏؜﻿]/g;

/** Persian ۰–۹ (U+06F0) and Arabic-Indic ٠–٩ (U+0660), both to Latin. */
function foldDigits(s: string): string {
  return s.replace(/[۰-۹٠-٩]/g, (d) => {
    const code = d.charCodeAt(0);
    const base = code >= 0x06f0 ? 0x06f0 : 0x0660;
    return String(code - base);
  });
}

/**
 * Fold a string to its matching form. Idempotent, and safe on text with no Persian in it.
 *
 * NFC first so that a decomposed sequence (base letter + combining hamza) becomes the composed
 * character the fold table knows about; without it أ typed as ا+ٕ would survive as ا plus a
 * combining mark, which `STRIP` then removes anyway — the two paths agree, but only by accident
 * unless the order is fixed here.
 */
export function foldPersian(input: string): string {
  if (!input) return '';
  let s = input.normalize('NFC');
  /**
   * Built from the table's own keys rather than written as a range.
   *
   * The first version used `[آ-ى]`, which is U+0622–U+0649 — and ARABIC YEH is U+064A, one code
   * point past the end. So the single most common collision in Persian text, ي vs ی, was the one
   * character the fold silently skipped. A class derived from the keys cannot drift from them.
   */
  s = s.replace(FOLD_PATTERN, (ch) => LETTER_FOLD[ch] ?? ch);
  s = s.replace(STRIP, '');
  s = foldDigits(s);
  return s;
}

/** True when the string contains any Arabic-script character — i.e. worth glossary expansion. */
export function hasPersian(input: string): boolean {
  return /[؀-ۿﭐ-﷿ﹰ-﻿]/.test(input);
}
